import { apiClient } from './api-client';
import type { StudentPaymentStatus } from '../utils/admin-labels';

export interface AdminDashboardMetrics {
  activeStudents: number;
  overdueStudents: number;
  upcomingDue: number;
  withoutActiveRoutine: number;
  brokenMachines: number;
  totalStudents: number;
  overdueList: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
  }>;
  upcomingList: Array<{
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    daysUntilDue: number;
    nextDueDate: string;
  }>;
}

interface StudentUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  licenseType?: string;
}

export async function fetchStudentPaymentStatuses(
  students: StudentUser[],
): Promise<Map<string, StudentPaymentStatus>> {
  const entries = await Promise.all(
    students.map(async (s) => {
      try {
        const status = await apiClient.get<StudentPaymentStatus>(
          `/api/payments/student/${s.id}/status`,
        );
        return [s.id, status] as const;
      } catch {
        return [
          s.id,
          {
            studentId: s.id,
            firstName: s.firstName,
            lastName: s.lastName,
            email: s.email,
            isActive: false,
            latestPayment: null,
            nextDueDate: null,
            daysUntilDue: null,
          },
        ] as const;
      }
    }),
  );
  return new Map(entries);
}

export async function fetchAdminDashboardMetrics(): Promise<AdminDashboardMetrics> {
  const [kpis, overdueRaw, upcomingRaw] = await Promise.all([
    apiClient.get<any>('/api/dashboard/admin-kpis'),
    apiClient.get<any[]>('/api/payments/overdue'),
    apiClient.get<any[]>('/api/payments/upcoming'),
  ]);

  return {
    activeStudents: kpis.activeStudents,
    overdueStudents: kpis.overdueStudents,
    upcomingDue: kpis.upcomingDueStudents,
    withoutActiveRoutine: kpis.studentsWithoutActiveRoutine,
    brokenMachines: kpis.brokenOrFaultyMachines,
    totalStudents: kpis.totalStudents,
    overdueList: overdueRaw.slice(0, 5),
    upcomingList: upcomingRaw.map(u => ({
      id: u.id,
      firstName: u.firstName,
      lastName: u.lastName,
      email: u.email,
      daysUntilDue: u.daysUntilDue,
      nextDueDate: u.nextDueDate,
    })).slice(0, 5),
  };
}
